// Internal navigation contract; logical IDs are validated by the caller once FE-01 fixes their format.
export interface MailNavigation {
  mailbox?: string
  folder?: string
  seen: 'all' | 'true' | 'false'
  q: string
  page: number
  thread?: string
  anchor?: string
  message?: string
}

export type MailNavigationResult = { ok: true; value: MailNavigation } | { ok: false; error: string }
export type LogicalIdValidator = (id: string) => boolean
export const mailUuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const isMailUuid = (id: string) => mailUuidPattern.test(id)
const keys = ['mailbox', 'folder', 'seen', 'q', 'page', 'thread', 'anchor', 'message'] as const

export function parseMailNavigation(search: string, validateLogicalId: LogicalIdValidator = isMailUuid): MailNavigationResult {
  const params = new URLSearchParams(search)
  const invalid = (): MailNavigationResult => ({ ok: false, error: 'Link mail non valido.' })
  if (Array.from(params.keys()).some(key => !(keys as readonly string[]).includes(key))) return invalid()
  if (keys.some(key => params.getAll(key).length > 1)) return invalid()
  const mailbox = params.get('mailbox') || undefined
  const thread = params.get('thread') || undefined
  const anchor = params.get('anchor') || undefined
  const message = params.get('message') || undefined
  if ([mailbox, thread, message].some(id => id !== undefined && !mailUuidPattern.test(id))) return invalid()
  if (anchor && (!validateLogicalId || !validateLogicalId(anchor))) return invalid()
  if ((thread || anchor || message) && !mailbox) return invalid()
  if ((anchor && !thread) || (message && (thread || anchor))) return invalid()
  const seen = params.get('seen') || 'all'
  if (seen !== 'all' && seen !== 'true' && seen !== 'false') return invalid()
  const rawPage = params.get('page') || '1'
  const page = Number(rawPage)
  if (!/^[1-9]\d*$/.test(rawPage) || !Number.isSafeInteger(page)) return invalid()
  const folder = params.get('folder') || undefined
  const q = params.get('q') || ''
  if ((folder?.length || 0) > 1024 || q.length > 200) return invalid()
  return { ok: true, value: { mailbox, folder, seen, q, page, thread, anchor, message } }
}

export function serializeMailNavigation(value: MailNavigation, validateLogicalId: LogicalIdValidator = isMailUuid): string {
  const params = new URLSearchParams()
  if (value.mailbox) params.set('mailbox', value.mailbox)
  if (value.folder) params.set('folder', value.folder)
  if (value.seen !== 'all') params.set('seen', value.seen)
  if (value.q) params.set('q', value.q)
  if (value.page !== 1) params.set('page', String(value.page))
  if (value.thread) params.set('thread', value.thread)
  if (value.anchor) params.set('anchor', value.anchor)
  if (value.message) params.set('message', value.message)
  if (!parseMailNavigation(params.toString(), validateLogicalId).ok) throw new Error('Link mail non valido.')
  const query = params.toString()
  return `/mail/inbox${query ? `?${query}` : ''}`
}

export function mailListNavigation(value: MailNavigation): MailNavigation {
  const { thread, anchor, message, ...list } = value
  return list
}
